import {
	section, div, img, a, button, p, span, ul, li
} from 'iblokz-snabbdom-helpers';
import dayjs from 'dayjs';
import 'dayjs/locale/bg';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import {resolveLocalized, DEFAULT_LOCALE} from '../../util/resolve-localized';
import eventTime from '../../util/event-time';

dayjs.extend(localizedFormat);

const AUTOPLAY_MS = 7000;

const copy = (i18n, key, fallback) => {
	const root = i18n && i18n.widget && i18n.widget.eventsCarousel;
	if (!root) return fallback;
	const parts = key.split('.');
	let cur = root;
	for (let i = 0; i < parts.length; i++) {
		if (cur == null) return fallback;
		cur = cur[parts[i]];
	}
	return cur == null || cur === '' ? fallback : cur;
};

const eventTitle = (event, lang) =>
	resolveLocalized(event && event.title, lang) || '';

const eventText = (event, lang) =>
	resolveLocalized(event && event.text, lang) || '';

const isTinker = event => {
	const slug = String((event && event.slug) || '');
	const title = eventTitle(event, 'en') + eventTitle(event, 'bg');
	return (event && event.type) === 'tinkering'
		|| /tinker/i.test(slug)
		|| /tinker/i.test(title);
};

const startMs = event => new Date((event && event.startDateTime) || 0).getTime();
const sortAsc = (a, b) => startMs(a) - startMs(b);
const sortDesc = (a, b) => startMs(b) - startMs(a);

const isUpcoming = event => {
	const end = (event && event.endDateTime) || (event && event.startDateTime);
	return new Date(end || 0).getTime() >= Date.now();
};

const cleanPlain = text => String(text || '')
	.replace(/[#>*_`\[\]()]/g, ' ')
	.replace(/[^\S\n]+/g, ' ')
	.replace(/ ?\n ?/g, '\n')
	.replace(/\n{3,}/g, '\n\n')
	.trim();

/** Visual cost: newlines spend a bit more budget than a single char. */
const NEWLINE_COST = 18;
const charCost = ch => (ch === '\n' ? NEWLINE_COST : 1);

const trimAtBoundary = (str, min = 40) => {
	const t = String(str || '').trimEnd();
	if (!t) return '';
	const at = Math.max(t.lastIndexOf('\n'), t.lastIndexOf(' '));
	return (at >= min ? t.slice(0, at) : t).trimEnd();
};

/** Truncate by visual budget; prefer breaking on newline/space; end with …. */
const excerptByBudget = (text, maxUnits) => {
	const plain = cleanPlain(text);
	if (!plain) return '';
	let units = 0;
	for (let i = 0; i < plain.length; i++) {
		units += charCost(plain[i]);
		if (units > maxUnits) {
			return `${trimAtBoundary(plain.slice(0, i))}…`;
		}
	}
	return plain;
};

/**
 * Budget for body copy based on other details-panel chrome
 * (series / title / sub / meta / CTA + gaps).
 */
const excerptForDetails = (text, {series, title, sub, meta} = {}) => {
	let maxUnits = 575;
	if (series) maxUnits -= 14;
	if (title) maxUnits -= Math.min(36, 12 + String(title).length * 0.2);
	if (sub) maxUnits -= Math.min(22, 10 + String(sub).length * 0.15);
	if (meta) maxUnits -= 10;
	maxUnits -= 20; // CTA + inner gaps
	return excerptByBudget(text, Math.max(320, Math.round(maxUnits)));
};

/** Split plain text into paragraph strings (no <br> inside <p>). */
const textParagraphs = text => String(text || '')
	.split(/\n+/)
	.map(s => s.trim())
	.filter(Boolean);

const eventTz = event => (event && event.timezone) || eventTime.DEFAULT_TZ;

const shortDate = (event, lang) => {
	dayjs.locale(lang === 'en' ? 'en' : 'bg');
	const tz = eventTz(event);
	const start = eventTime.parse(event && event.startDateTime, tz);
	if (!start) return '';
	const end = eventTime.parse(event && event.endDateTime, tz);
	if (end && !end.isSame(start, 'day')) {
		return `${start.format('DD.MM')}–${end.format('DD.MM')}`;
	}
	return start.format('DD.MM');
};

const metaLine = (event, lang, kind, i18n) => {
	dayjs.locale(lang === 'en' ? 'en' : 'bg');
	const start = eventTime.parse(event && event.startDateTime, eventTz(event));
	const bits = [];
	if (kind === 'regular') {
		bits.push(copy(i18n, 'regular', 'регулярен'));
	}
	if (start) {
		bits.push(start.format('DD.MM'));
		bits.push(start.format('HH:mm'));
	}
	return bits.filter(Boolean).join(' · ');
};

const thumbLabel = (item, lang, i18n) => {
	const {event, kind} = item;
	const title = eventTitle(event, lang);
	const short = title
		.replace(/\s*[|/].*$/, '')
		.replace(/^MusicTechBG\s+/i, '')
		.trim();
	if (kind === 'regular') {
		return `${copy(i18n, 'regular', 'регулярен')} ${short || 'Tinkerday'}`;
	}
	const when = shortDate(event, lang);
	return [when, short].filter(Boolean).join(' ');
};

const ctaLabel = (event, i18n) => {
	const type = (event && event.type) || '';
	return copy(i18n, `cta.${type}`, null)
		|| copy(i18n, 'cta.default', 'към събитието');
};

const seriesHint = (event, lang) => {
	const title = eventTitle(event, lang);
	if (/sound\s*currents/i.test(title)) return 'Sound Currents';
	if (/sofia\s*experimental/i.test(title)) return 'Sofia Experimental';
	if (isTinker(event)) return 'Tinkerday';
	return '';
};

const cleanTitle = (event, lang) => {
	const title = eventTitle(event, lang);
	const series = seriesHint(event, lang);
	let cleaned = title.replace(/\s*[|/].*$/, '').trim() || title;
	if (series && /sound\s*currents/i.test(title)) {
		cleaned = title
			.replace(/\s*[|].*Sound Currents.*$/i, '')
			.replace(/\s*\|.*$/i, '')
			.trim() || title;
	}
	const split = cleaned.match(/^([^:—\-]+)[:—\-]\s*(.+)$/);
	if (split) {
		return {headline: split[1].trim(), sub: split[2].trim()};
	}
	return {headline: cleaned, sub: ''};
};

const pickCarouselItems = (list = []) => {
	const upcoming = list.filter(isUpcoming).sort(sortAsc);
	const tinkers = list.filter(isTinker).sort(sortDesc);
	const items = [];
	const seen = {};

	const push = (event, kind) => {
		const id = String((event && (event._id || event.slug)) || '');
		if (!event || !id || seen[id]) return;
		seen[id] = true;
		items.push({event, kind});
	};

	upcoming.filter(e => !isTinker(e)).slice(0, 2).forEach(e => push(e, 'upcoming'));
	upcoming.forEach(e => {
		if (items.length >= 3) return;
		push(e, isTinker(e) ? 'regular' : 'upcoming');
	});

	const tinker = upcoming.find(isTinker) || tinkers[0];
	if (tinker) {
		const id = String(tinker._id || tinker.slug || '');
		const existing = items.find(i => String(i.event._id || i.event.slug) === id);
		if (existing) {
			existing.kind = 'regular';
		} else if (items.length < 3) {
			push(tinker, 'regular');
		} else {
			items[items.length - 1] = {event: tinker, kind: 'regular'};
		}
	}

	return items.slice(0, 3);
};

const goEvent = (actions, event) => {
	const path = (event && (event.slug || event._id)) || '';
	if (!path) return;
	actions.router.go(`calendar.${path}`);
};

/** Blurred letterbox layers (.media-backlight host + these children). */
const mediaBacklight = (src, fg) => [].concat(
	div('.media-backlight-back', {
		style: {backgroundImage: `url("${src}")`}
	}),
	div('.media-backlight-scrim'),
	fg
);

const clearAutoplay = el => {
	if (el && el._mtbgAutoplay) {
		clearInterval(el._mtbgAutoplay);
		el._mtbgAutoplay = null;
	}
};

const startAutoplay = (el, actions, count) => {
	clearAutoplay(el);
	if (!el || count < 2) return;
	el._mtbgCount = count;
	el._mtbgActions = actions;
	el._mtbgAutoplay = setInterval(() => {
		if (el._mtbgPaused) return;
		const total = el._mtbgCount || count;
		const act = el._mtbgActions || actions;
		act.set(['home'], cur => {
			const i = Number(cur && cur.carouselIndex) || 0;
			const n = (i + 1) % total;
			return {carouselIndex: n, carouselDir: 'next'};
		});
	}, AUTOPLAY_MS);
};

/** Keep timer alive across patches (empty→loaded never re-fires insert). */
const ensureAutoplay = (el, actions, count) => {
	if (!el) return;
	if (count < 2) {
		clearAutoplay(el);
		el._mtbgCount = count;
		return;
	}
	el._mtbgActions = actions;
	if (!el._mtbgAutoplay || el._mtbgCount !== count) {
		startAutoplay(el, actions, count);
	}
};

const rootHooks = actions => ({
	insert: vnode => {
		const el = vnode.elm;
		const count = Number(el && el.getAttribute('data-count')) || 0;
		ensureAutoplay(el, actions, count);
	},
	postpatch: (oldVnode, vnode) => {
		const el = vnode.elm;
		const count = Number(el && el.getAttribute('data-count')) || 0;
		ensureAutoplay(el, actions, count);
	},
	destroy: vnode => {
		clearAutoplay(vnode.elm);
	}
});

/** Pause only while the thumb strip is hovered — not the whole hero. */
const stripHooks = {
	insert: vnode => {
		const strip = vnode.elm;
		const root = strip && strip.closest('.events-carousel');
		if (!root) return;
		strip._mtbgOnEnter = () => {
			root._mtbgPaused = true;
		};
		strip._mtbgOnLeave = () => {
			root._mtbgPaused = false;
		};
		strip.addEventListener('mouseenter', strip._mtbgOnEnter);
		strip.addEventListener('mouseleave', strip._mtbgOnLeave);
	},
	destroy: vnode => {
		const strip = vnode.elm;
		if (strip && strip._mtbgOnEnter) {
			strip.removeEventListener('mouseenter', strip._mtbgOnEnter);
			strip.removeEventListener('mouseleave', strip._mtbgOnLeave);
		}
	}
};

export default ({state, actions, i18n}) => {
	const lang = state.lang || DEFAULT_LOCALE;
	const list = (state.events && state.events.list) || [];
	const items = pickCarouselItems(list);
	const rawIndex = Number((state.home && state.home.carouselIndex) || 0);
	const index = items.length
		? Math.max(0, Math.min(items.length - 1, rawIndex))
		: 0;
	const dir = (state.home && state.home.carouselDir) || 'next';

	const setIndex = next => {
		if (!items.length) return;
		const n = ((next % items.length) + items.length) % items.length;
		if (n === index) return;
		let resolved = 'next';
		if (index === items.length - 1 && n === 0) resolved = 'next';
		else if (index === 0 && n === items.length - 1) resolved = 'prev';
		else if (n < index) resolved = 'prev';
		actions.set(['home'], {
			carouselIndex: n,
			carouselDir: resolved
		});
		// Restart the 7s window after manual switch
		const root = typeof document !== 'undefined'
			&& document.querySelector('.events-carousel[data-count]');
		if (root) startAutoplay(root, actions, items.length);
	};

	if (!items.length) {
		return section('.events-carousel.is-empty', [
			div('.events-carousel-empty', copy(i18n, 'empty', 'Няма предстоящи събития засега.'))
		]);
	}

	return section('.events-carousel', {
		attrs: {'data-count': String(items.length)},
		class: {
			'is-dir-next': dir !== 'prev',
			'is-dir-prev': dir === 'prev'
		},
		hook: rootHooks(actions)
	}, [
		div('.events-carousel-stage', [
			div('.events-carousel-poster', [
				div('.events-carousel-poster-stack', items.map((item, i) => {
					const title = cleanTitle(item.event, lang).headline;
					const src = item.event.poster || '';
					const kids = src
						? mediaBacklight(src, img('.events-carousel-poster-fg', {
							attrs: {
								src,
								alt: title,
								draggable: 'false'
							}
						}))
						: [div('.events-carousel-poster-fallback', title)];
					return div('.events-carousel-poster-slide', {
						key: `poster-${item.event.slug || item.event._id || i}`,
						class: {
							'is-active': i === index,
							'media-backlight': !!src
						}
					}, kids);
				}))
			]),
			div('.events-carousel-details-panel', [
				div('.events-carousel-details-stack', items.map((item, i) => {
					const ev = item.event;
					const titles = cleanTitle(ev, lang);
					const ser = seriesHint(ev, lang);
					const when = metaLine(ev, lang, item.kind, i18n);
					const body = excerptForDetails(eventText(ev, lang), {
						series: ser,
						title: titles.headline,
						sub: titles.sub,
						meta: when
					});
					const link = `/calendar/${ev.slug || ev._id}`;
					return div('.events-carousel-details', {
						key: `details-${ev.slug || ev._id || i}`,
						class: {'is-active': i === index}
					}, [
						div('.events-carousel-details-inner', [
							ser
								? p('.events-carousel-series', `«${ser}»`)
								: '',
							p('.events-carousel-title', titles.headline),
							titles.sub
								? p('.events-carousel-sub', titles.sub)
								: '',
							when
								? p('.events-carousel-meta', when)
								: '',
							body
								? div('.events-carousel-text-block',
									textParagraphs(body).map(para =>
										p('.events-carousel-text', para)
									)
								)
								: '',
							a('.events-carousel-cta', {
								attrs: {href: link},
								on: {
									click: evnt => {
										evnt.preventDefault();
										goEvent(actions, ev);
									}
								}
							}, [
								span(ctaLabel(ev, i18n)),
								span('.events-carousel-cta-arrow', '→')
							])
						])
					]);
				}))
			])
		]),
		div('.events-carousel-strip', {
			hook: stripHooks
		}, [
			div('.events-carousel-strip-media', [
				button('.events-carousel-nav.prev', {
					attrs: {
						type: 'button',
						title: copy(i18n, 'prev', 'Предишно'),
						'aria-label': copy(i18n, 'prev', 'Предишно')
					},
					on: {click: () => setIndex(index - 1)}
				}, '‹'),
				ul('.events-carousel-thumbs', items.map((item, i) =>
					li('.events-carousel-thumb', {
						class: {'is-active': i === index}
					}, [
						button({
							attrs: {
								type: 'button',
								'aria-label': thumbLabel(item, lang, i18n)
							},
							on: {click: () => setIndex(i)}
						}, [
							span('.events-carousel-thumb-frame', {
								class: {'media-backlight': !!item.event.poster}
							}, item.event.poster
								? mediaBacklight(item.event.poster, img({
									attrs: {
										src: item.event.poster,
										alt: '',
										draggable: 'false'
									}
								}))
								: [span('.events-carousel-thumb-ph')]
							)
						])
					])
				)),
				button('.events-carousel-nav.next', {
					attrs: {
						type: 'button',
						title: copy(i18n, 'next', 'Следващо'),
						'aria-label': copy(i18n, 'next', 'Следващо')
					},
					on: {click: () => setIndex(index + 1)}
				}, '›')
			]),
			ul('.events-carousel-thumb-labels', items.map((item, i) =>
				li('.events-carousel-thumb-label-item', {
					class: {'is-active': i === index}
				}, [
					button({
						attrs: {type: 'button'},
						on: {click: () => setIndex(i)}
					}, [
						span('.events-carousel-thumb-label', thumbLabel(item, lang, i18n))
					])
				])
			))
		])
	]);
};
